import { useCallback, useState, useMemo, useEffect } from 'react';
import styles from './GenerateStudentLinkModal.module.scss';
import './customStyle.css';
import Modal from 'src/components/modal/Modal';
import Button from 'src/components/button/Button';
import CloseIcon from '@src/assets/icons/close.svg?react';
import { TicketInput } from './text-input/TicketInput';
import { TextInputValue } from '@src/components/text-input/TextInput';
import { Slider } from 'antd';
import { AbsentStudentLesson, useCreateAbsentStudentLessonMutation } from '@src/api/graphql-codegen';
import { isJSON } from '@src/utils/validation';
import Select from '../select/Select';
import { SelectOption } from '@src/types';
import { newAUStandaloneLessonAgeGroupOptions, NAStandaloneLessonAgeGroupOptions } from '@src/constants';
import { useUser } from '@src/hooks';
import { logger } from '../../utils/logger';

type Props = {
  open: boolean;
  lessonId?: string;
  isSll: boolean;
  onClose: () => void;
  onActiveStudentLink: (feedback: AbsentStudentLesson) => void;
  programId?: string;
  programName?: string;
  unitId?: string;
  unitName?: string;
  className?: string;
  formData?: string | null;
  gradeName?: string;
};

const validationErrors = {
  tickets: '',
  minutes: '',
  className: '',
};

type ErrorKeys = keyof typeof validationErrors;

function GenerateStudentLinkModal(props: Props) {
  const {
    open,
    lessonId,
    isSll,
    programId,
    programName,
    unitId,
    unitName,
    formData,
    onClose,
    onActiveStudentLink,
    gradeName,
  } = props;

  const defaultNumberOfStudents = 1

  const [tickets, setTickets] = useState(defaultNumberOfStudents);
  const [duration, setDuration] = useState(2);
  const [timeLabel, setTimeLabel] = useState('Days');
  const [closeMenu, setCloseMenu] = useState(false);
  const [linkCount, setLinkCount] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<ErrorKeys, string>>>(validationErrors);
  const [beError, setBeError] = useState('');
  const [grade, setGrade] = useState('');
  const [step, setStep] = useState(0);

  const [user] = useUser();

  const isAUuser = useMemo(() => user?.id?.toLowerCase().startsWith('au'), [user?.id]);

  useEffect(() => {
    if (isJSON(gradeName)) {
      const grades = JSON.parse(gradeName || '[]');
      if (grades.length > 0) {
        setGrade(grades[0]);
      }
    }
  }, [gradeName])

  const grades = useMemo(() => {
    if (isJSON(gradeName)) {
      const grades = JSON.parse(gradeName || '[]');

      return grades.map((grade: string) => {
        return {
          label: grade,
          value: grade
        }
      });
    }
    return isAUuser ? newAUStandaloneLessonAgeGroupOptions : NAStandaloneLessonAgeGroupOptions
  }, [gradeName, isAUuser])

  const [createAbsentStudentLesson, { loading }] = useCreateAbsentStudentLessonMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
    },
    onCompleted(res) {
      onActiveStudentLink(res.createAbsentStudentLesson as AbsentStudentLesson);
      onClose();
    }
  });

  const handleChangeText = useCallback((setter: any, key: ErrorKeys) => (value: TextInputValue) => {
    setErrors({
      ...errors,
      [key]: '',
    });
    if (key === 'tickets') {
      if (Number(value) > 0) {
        setter(Number(value));
      }
    } else {
      setter(value);
    }
  }, [errors]);

  const handleSliderChange = useCallback((setter: any) => (value: any) => {
    setter(Number(value))
  }, [])

  const handleChangeLabel = useCallback((value: any) => {
    setTimeLabel(value)
  }, [])

  const handleCloseTimeMenu = () => {
    setCloseMenu(current => !current)
  }

  const handleNext = useCallback(() => {
    if (step === 0 && grade) {
      setStep(1)
    } else if (step === 1) {
      setStep(2)
    }
  }, [step, grade])

  const handleChangeGrade = (group: SelectOption) => {
    setGrade(group.value)
  }

  const handleContinue = useCallback(() => {
    if (!linkCount) {
      setLinkCount(true)
      return;
    }
    setBeError('');

    const expiration = timeLabel === "Hours" ? duration * 60 : duration * 60 * 24;

    createAbsentStudentLesson({
      variables: {
        input: {
          expiryMinutes: expiration,
          isSLL: isSll,
          lessonId: lessonId || '',
          studentsCount: tickets,
          programId,
          programName,
          unitId,
          unitName,
          formData,
          grade: grade !== '' ? grade.replace(/\s*Program/, "") : gradeName?.replace(/\s*Program/, "") ?? '',
        }
      }
    });
  }, [
    tickets,
    isSll,
    timeLabel,
    duration,
    linkCount,
    lessonId,
    programId,
    programName,
    unitId,
    unitName,
    formData,
    createAbsentStudentLesson,
    grade,
    gradeName,
  ]);
  logger.info('Link Count and step ====', linkCount, step);

  const modalContent = useMemo(() => {
    if (((isJSON(gradeName) && grades.length > 1) || !gradeName) && step === 0) {
      return (
        <>
          <div className={styles.header}>
            <div className={styles.preProTitle}>{'Select Your Grade'}</div>
          </div>
          <div className={styles.inputView}>
            <Select
              menuClassName={styles.selectorStyle}
              className={styles.gradeView}
              showSearch={false} inline={false} doNotSort={true}
              placeholder={ 'Select Your Grade Level'}
              value={grade} options={grades}
              onChange={handleChangeGrade}
            />
          </div>
          <div className={styles.content}>
            <div className={styles.action}>
              <Button disabled={false} size="small" className={styles.startButton} onClick={handleNext} label={'CONTINUE'} />
            </div>
          </div>
        </>
      )
    } 
    if (!linkCount && (((grades.length > 0) && step === 1) || step === 0)) {
      return (
        <>
          <div className={styles.header}>
            <div className={styles.title}>{'Create Student Link'}</div>
            <div className={styles.normalText}>
            Select the number of Students who require access to this lesson. This would typically be the number of students who missed your class.
            </div>
          </div>
          <div className={styles.editView}>
            <TicketInput autoFocus={true} hasError={false} subTitle={''} type={'number'} id={'tickets'} label={'Students'} value={tickets ?? defaultNumberOfStudents} onChange={handleChangeText(setTickets, 'tickets')} />
            <div className={styles.sliderView}>
              <span>{tickets} {tickets === 1 ? 'Student Link' : 'Student Links'}</span>
              <Slider defaultValue={defaultNumberOfStudents} value={tickets} min={1} max={300} onChange={handleSliderChange(setTickets)} trackStyle={{ background: "#FFB073" }} />
            </div>
          </div>
          <div className={styles.content}>
            <div className={styles.action}>
              <Button disabled={false} size="small" className={styles.startButton} onClick={handleContinue} label={'CONTINUE'} />
            </div>
          </div>
        </>
      )
    } 
    if (linkCount) {
      return (
        <>
          <div className={styles.header}>
            <div className={styles.title}>{'Create Student Link'}</div>
            <div className={styles.normalText}>
              Select the <strong>duration</strong> for this link to be active. After the time elapses, the lesson will no longer be accessible.
            </div>
          </div>
          <div className={styles.editView}>
            <TicketInput autoFocus={true} hasError={false} subTitle={''} type={'number'} id={'hours'} label={timeLabel} value={duration ?? 30} closeMenu={closeMenu} onChange={handleChangeText(setDuration, 'tickets')} setTimeChange={handleChangeLabel} />
            <div className={styles.sliderView}>
              <span>{duration ?? 2} {duration === 1 && timeLabel === 'Days' ? 'Day': timeLabel}</span>
              <Slider defaultValue={2} value={duration} min={1} max={timeLabel === "Hours" ? 24 : 5} onChange={handleSliderChange(setDuration)} trackStyle={{ background: "#FFB073" }} />
            </div>
          </div>
          <div className={styles.content}>
            {Boolean(beError) && <div className={styles.error}>{beError}</div>}
            <div className={styles.action}>
              <Button disabled={loading} size="small" onClick={handleContinue} isLoading={loading} label={`Activate Link for ${duration} ${duration === 1 && timeLabel === 'Days' ? 'Day': timeLabel} only`} />
            </div>
          </div>
        </>
      )
    }
  }, [gradeName, grades, step, linkCount, grade, handleNext, tickets, handleChangeText, handleSliderChange, handleContinue, timeLabel, duration, closeMenu, handleChangeLabel, beError, loading])

  return (
    <Modal global={false} open={open} onClose={onClose} closeButton={false} className={styles.wrapper} >
      <div className={styles.mainView} onClick={handleCloseTimeMenu}>
        <button onClick={onClose} className={styles.closeWrapper}>
          <CloseIcon className={styles.closeIcon} />
        </button>
        {modalContent}
      </div>
    </Modal>
  );
}

export default GenerateStudentLinkModal;
